import React, { useState } from 'react';
import {
  dompurifier,
  useActivityTracker
} from '@nile/components/src/Utils/helpers';
import { getTranslatedText, useIntl } from '@nile/components';
import markdownit from 'markdown-it';
import { GET_AI_WEB_SEARCH_RECOMMENDATION_REPLY } from '@nile/graphql';
import { Button, TextStyle } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import { useAIStore } from '@nile/components/src/Components/AI/store/aiStore';
import SendReplyCard from './SendReplyCard';
import DislikeCard from './DislikeCard';
import { FormattedMessage } from 'react-intl';
import { useAIRecommendedSolutionsStore } from '@nile/components/src/Components/AI/store/aiRecommendedSolutionsStore.tsx';

const getImageAndLinkHtml = ({ domain, link }) => {
  if (domain && link) {
    const imageLink = `https://www.google.com/s2/favicons?domain=${domain}`;
    const htmlString = `<div class='citation-wrapper flex g5 pt-5 pb-5'>
      <div class='flex actr'>
        <img src=${imageLink} class='citation-image' />
      </div>
      <div class='ellipsis'>
        <a class='citation-link' href=${link} target='_blank'>${link}</a>
      </div>
    </div>`;
    return htmlString;
  }
  return null;
};

const getCitations = (text, citations) => {
  return text.replace(/\[(\d+)\]/g, (match, number) => {
    const citation = citations?.[number];
    if (citation) {
      const citationHtml = getImageAndLinkHtml(citation);
      return citationHtml;
    }
    return '';
  });
};

function formatTextToHtml({ text, citations }) {
  const formattedText = text;

  const md = markdownit({
    html: true,
    linkify: true,
    typographer: true
  });

  // enable everything
  const htmlText = md.render(formattedText);
  const htmlWithCitation = getCitations(htmlText, citations);
  return htmlWithCitation;
}

const getHtmlFromMarkDown = (markdown) => {
  const md = markdownit({
    html: true,
    linkify: true,
    typographer: true
  });

  md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
    const aIndex = tokens[idx].attrIndex('target');
    if (aIndex < 0) {
      tokens[idx].attrPush(['target', '_blank']); // Add target="_blank" if not present
    }
    return self.renderToken(tokens, idx, options);
  };

  // enable everything
  const htmlText = md.render(markdown);
  return htmlText;
};

const AIWebSearchResults = ({ data, onWebSearchDislike, ticketId }) => {
  // const formattedResult = formatTextToHtml({
  //   text: data?.getAIWebSearchRecommendation || '',
  //   citations: data?.citations
  // });
  const intl = useIntl();

  const webSolutionsLoadingText = [
    getTranslatedText(
      intl,
      'ai.recommended.solutions.web.search.reply.loading.message.1'
    ),
    getTranslatedText(
      intl,
      'ai.recommended.solutions.web.search.reply.loading.message.2'
    ),
    getTranslatedText(
      intl,
      'ai.recommended.solutions.web.search.reply.loading.message.3'
    ),
    getTranslatedText(
      intl,
      'ai.recommended.solutions.web.search.reply.loading.message.4'
    )
  ];

  const formattedResult = getHtmlFromMarkDown(
    data?.getAIWebSearchRecommendation
  );

  const showSuggestedReply = useAIRecommendedSolutionsStore(
    (s) => s.showSuggestedReply
  );
  const setShowSuggestedReply = useAIRecommendedSolutionsStore(
    (s) => s.setShowSuggestedReply
  );

  const sendWebSearchReplyResponse = useAIRecommendedSolutionsStore(
    (s) => s.sendWebSearchReplyResponse
  );

  const sendWebSearchReplyData = useAIRecommendedSolutionsStore(
    (s) => s.sendWebSearchReplyData
  );

  const setSendWebSearchReplyResponse = useAIRecommendedSolutionsStore(
    (s) => s.setSendWebSearchReplyResponse
  );

  const setSendWebSearchReplyData = useAIRecommendedSolutionsStore(
    (s) => s.setSendWebSearchReplyData
  );

  const resetData = () => {
    setShowSuggestedReply(false);
    // Revisit
    setSendWebSearchReplyResponse(null);
    setSendWebSearchReplyData(null);
  };

  const [dislike, setDislike] = useState(false);

  const setShowRatingComponent = useAIStore((s) => s.setShowRatingComponent);

  const showRating = () => {
    setShowRatingComponent(true);
  };

  const onDislikeClick = () => {
    setDislike(true);
    useActivityTracker({
      event: 'Clicked on solution not helpful from web search results'
    });
    setTimeout(() => {
      if (onWebSearchDislike) {
        onWebSearchDislike(data);
      }
    }, 1200);
  };

  if (dislike) {
    return <DislikeCard />;
  }

  return (
    <>
      <div id="ai-web-result-wrapper">
        <div className="p10 br10 ai-dark-bg">
          <div className="flex g5 actr mb10">
            <div className="flex">
              <SVGIcon name="AI_Bulb" />
            </div>
            <div className="flex">
              <TextStyle weight="Medium">Web solution</TextStyle>
            </div>
          </div>
          <div
            className="citation-html"
            dangerouslySetInnerHTML={{ __html: dompurifier(formattedResult) }}
          />

          {showSuggestedReply ? (
            <div className="mt10">
              <SendReplyCard
                query={GET_AI_WEB_SEARCH_RECOMMENDATION_REPLY}
                queryVariables={{
                  recommendation: data?.getAIWebSearchRecommendation,
                  ticketId: ticketId
                }}
                accessor="getAIWebSearchRecommendationReply"
                loadingText={webSolutionsLoadingText}
                collapeOnInsert
                onCopy={showRating}
                onCollapseComplete={() => {
                  resetData();
                }}
                mixpanelEventOnInsert={{
                  event:
                    'Insert text from reply generated from web search results'
                }}
                sendReplyData={sendWebSearchReplyData}
                sendReplyResponse={sendWebSearchReplyResponse}
                setSendReplyData={setSendWebSearchReplyData}
                setSendReplyResponse={setSendWebSearchReplyResponse}
              />
            </div>
          ) : (
            <div className="flex actr g10 mt10">
              <div className="flex w100 ai-button-big-bg">
                <Button
                  aiPrimary
                  icon={<SVGIcon name="AIWand" size="sm" />}
                  size="slim"
                  fullWidth
                  onClick={() => {
                    resetData();
                    setShowSuggestedReply(true);
                    useActivityTracker({
                      event: 'Clicked on send reply from web search results'
                    });
                  }}
                >
                  <div className="fsize12">
                    <FormattedMessage id="common.send.reply" />
                  </div>
                </Button>
              </div>
              <div className="flex">
                <Button
                  aiSecondary
                  size="tiny"
                  aiOutline
                  onClick={onDislikeClick}
                >
                  <div className="flex">
                    <SVGIcon name="ThumbsDownOutline" />
                  </div>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AIWebSearchResults;
